import axios from 'axios';
import { PolicyData } from '../../../../components/AccountOnboarding/ManualAccountOnboarding/JSONContainer';
import { ManualArnProps } from '../../../../schemas/account.schema';
import { accountService } from '../../../../services';

interface Iconfig {
  addManualArnHandler: (params: ManualArnProps) => Promise<any>;
}

export const config: Iconfig = {
  addManualArnHandler: (params: ManualArnProps) =>
    accountService.createManualArnAccount(params),
};

export const triggerAwsJob = (accountId: string) => {
  const apiUrl = `https://cloudforestx-aws-resource-api.cloudforestx.com/api/aws/awsJob?accountId=${accountId}&type=night`;
  axios.get(apiUrl);
};

export const validateAwsArn = (arn: string): boolean => {
  // AWS ARN format: arn:partition:service:region:account-id:resource-id
  // Example: arn:aws:iam::123456789012:role/service-role/AmazonEKS-NodegroupRole
  const arnRegex = /^arn:aws:[a-z0-9-]+:[a-z0-9-]*:[0-9]{12}:.+$/i;
  return arnRegex.test(arn);
};

// Function to extract account ID from ARN
export const extractAccountIdFromArn = (arn: string): string | null => {
  // ARN format: arn:aws:iam::123456789012:role/role-name
  const matches = arn.match(/^arn:aws:[a-z0-9-]+:[a-z0-9-]*:([0-9]{12}):.+$/i);
  return matches ? matches[1] : null;
};

export const CUSTOM_POLICIES: PolicyData[] = [
  {
    name: 'Write Access Policy',
    suggestedName: 'cfx-write-access-policy',
    description:
      'This policy allows write access to specific AWS resources required by CloudForestX.',
    json: `{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Action": [
                "sns:TagResource",
                "elasticfilesystem:UntagResource",
                "ec2:DeleteTags",
                "sqs:UntagQueue",
                "s3:ReplicateTags",
                "elasticbeanstalk:AddTags",
                "sns:UntagResource",
                "resource-groups:Untag",
                "globalaccelerator:TagResource",
                "elasticfilesystem:DeleteTags",
                "elasticloadbalancing:RemoveTags",
                "dynamodb:TagResource",
                "s3:DeleteJobTagging",
                "s3:PutBucketTagging",
                "s3:PutObjectTagging",
                "fsx:TagResource",
                "resource-groups:Tag",
                "resource-groups:GetTags",
                "rds:AddTagsToResource",
                "s3:DeleteObjectTagging",
                "elasticbeanstalk:UpdateTagsForResource",
                "elasticache:RemoveTagsFromResource",
                "dynamodb:UntagResource",
                "s3:DeleteStorageLensConfigurationTagging",
                "ec2:CreateTags",
                "fsx:UntagResource",
                "s3:PutStorageLensConfigurationTagging",
                "elasticbeanstalk:RemoveTags",
                "s3:PutObjectVersionTagging",
                "glacier:AddTagsToVault",
                "s3:PutJobTagging",
                "globalaccelerator:UntagResource",
                "s3:DeleteObjectVersionTagging",
                "elasticache:AddTagsToResource",
                "elasticfilesystem:TagResource",
                "elasticfilesystem:CreateTags",
                "sqs:TagQueue",
                "sqs:ListQueueTags",
                "workspaces:CreateTags",
                "workspaces:DeleteTags",
                "elasticloadbalancing:AddTags",
                "glacier:RemoveTagsFromVault",
                "elasticmapreduce:RemoveTags",
                "elasticmapreduce:AddTags",
                "rds:RemoveTagsFromResource",
                "ecr:ListTagsForResource",
                "ecs:ListContainerInstances"
            ],
            "Resource": "*",
            "Effect": "Allow",
            "Sid": "TaggingPermissions"
        }
    ]
}`,
    copied: false,
  },
  {
    name: 'Resource Read Policy',
    suggestedName: 'cfx-resource-read-policy',
    description:
      'This policy allows read access to AWS resources for inventory and monitoring.',
    json: `{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Action": [
                "ec2:DescribeInstances",
                "elasticmapreduce:ListBootstrapActions",
                "autoscaling:DescribePolicies",
                "iam:ListServerCertificates",
                "ecr-public:DescribeRegistries",
                "cloudformation:DescribeStackResource",
                "ec2:DescribeSnapshots",
                "elasticmapreduce:ListInstanceGroups",
                "elasticloadbalancing:DescribeLoadBalancers",
                "elasticmapreduce:ListInstances",
                "elasticmapreduce:ListSecurityConfigurations",
                "autoscaling:DescribeAutoScalingGroups",
                "iam:ListAttachedRolePolicies",
                "logs:GetLogEvents",
                "ec2:DescribeAccountAttributes",
                "autoscaling:DescribeNotificationConfigurations",
                "elasticloadbalancing:DescribeInstanceHealth",
                "ec2:DescribeKeyPairs",
                "iam:ListRolePolicies",
                "cloudformation:ListStackResources",
                "elasticmapreduce:DescribeSecurityConfiguration",
                "cloudtrail:LookupEvents",
                "iam:GetRole",
                "sns:ListSubscriptionsByTopic",
                "rds:DescribeDBSnapshots",
                "ec2:DescribeLaunchTemplates",
                "elasticmapreduce:ViewEventsFromAllClustersInConsole",
                "elasticmapreduce:ListSteps",
                "cloudformation:DescribeStackResources",
                "elasticbeanstalk:Describe*",
                "autoscaling:DescribeLoadBalancers",
                "cloudwatch:GetMetricStatistics",
                "ecr-public:GetRepositoryPolicy",
                "cloudformation:DescribeStacks",
                "cloudformation:GetTemplate",
                "elasticbeanstalk:List*",
                "cloudwatch:DescribeAlarms",
                "rds:DescribeOrderableDBInstanceOptions",
                "globalaccelerator:Describe*",
                "ec2:DescribeSubnets",
                "cloudformation:ValidateTemplate",
                "elasticmapreduce:GetManagedScalingPolicy",
                "elasticbeanstalk:Check*",
                "autoscaling:DescribeAutoScalingInstances",
                "ec2:DescribeAddresses",
                "elasticmapreduce:GetBlockPublicAccessConfiguration",
                "rds:DescribeDBEngineVersions",
                "logs:DescribeLogStreams",
                "ec2:DescribeInstanceAttribute",
                "autoscaling:DescribeLaunchConfigurations",
                "sns:ListTopics",
                "ec2:DescribeSpotInstanceRequests",
                "elasticmapreduce:DescribeCluster",
                "cloudwatch:ListMetrics",
                "elasticbeanstalk:RequestEnvironmentInfo",
                "elasticmapreduce:DescribeStep",
                "autoscaling:DescribeScalingActivities",
                "ec2:DescribeAvailabilityZones",
                "autoscaling:DescribeAccountLimits",
                "rds:DescribeDBInstances",
                "autoscaling:DescribeScheduledActions",
                "acm:ListCertificates",
                "elasticmapreduce:DescribeEditor",
                "elasticmapreduce:ListClusters",
                "elasticfilesystem:DescribeFileSystems",
                "sts:GetServiceBearerToken",
                "ec2:DescribeInstanceStatus",
                "ecr-public:DescribeImageTags",
                "elasticloadbalancing:DescribeSSLPolicies",
                "ecr-public:DescribeImages",
                "ecr-public:GetAuthorizationToken",
                "cloudformation:ListStacks",
                "sqs:ListQueues",
                "ecr-public:GetRepositoryCatalogData",
                "ec2:DescribeLaunchTemplateVersions",
                "elasticmapreduce:ListInstanceFleets",
                "iam:ListRoles",
                "ec2:DescribeClassicLinkInstances",
                "iam:ListInstanceProfiles",
                "ec2:DescribeSecurityGroups",
                "ec2:DescribeImages",
                "ecr-public:DescribeRepositories",
                "globalaccelerator:List*",
                "s3:ListAllMyBuckets",
                "ec2:DescribeVpcs",
                "ecr-public:GetRegistryCatalogData",
                "elasticloadbalancing:DescribeTargetHealth",
                "elasticloadbalancing:DescribeTargetGroups",
                "pricing:*",
                "elasticbeanstalk:RetrieveEnvironmentInfo",
                "elasticmapreduce:ListEditors",
                "ecr-public:BatchCheckLayerAvailability",
                "elasticmapreduce:DescribeJobFlows"
            ],
            "Resource": "*",
            "Effect": "Allow"
        },
        {
            "Action": [
                "s3:GetObjectAcl",
                "s3:GetObject",
                "s3:GetObjectVersionAcl",
                "s3:ListBucket",
                "s3:GetBucketLocation",
                "s3:GetBucketPolicy",
                "s3:GetObjectVersion"
            ],
            "Resource": "*",
            "Effect": "Allow"
        }
    ]
}`,
    copied: false,
  },
  {
    name: 'Invoice Policy',
    suggestedName: 'cfx-invoice-policy',
    description:
      'This policy allows access to AWS Cost and Usage Reports for invoice generation.',
    json: `{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Action": "cloudwatch:GetInsightRuleReport",
            "Resource": "arn:aws:cloudwatch:*:*:insight-rule/DynamoDBContributorInsights*",
            "Effect": "Allow",
            "Sid": "VisualEditor0"
        },
        {
            "Action": [
                "fsx:Describe*",
                "eks:ListTagsForResource",
                "glacier:GetVaultAccessPolicy",
                "s3:Get*",
                "lambda:GetFunctionConfiguration",
                "cloudwatch:Describe*",
                "s3:List*",
                "glacier:ListParts",
                "dynamodb:Describe*",
                "ec2:DescribeInternetGateways",
                "kinesis:ListStreams",
                "rds:Describe*",
                "resource-groups:GetGroup",
                "kinesis:DescribeStreamSummary",
                "cloudwatch:DescribeAlarmsForMetric",
                "dax:Query",
                "dynamodb:GetItem",
                "ec2:DescribeAccountAttributes",
                "elasticloadbalancing:Describe*",
                "workspaces:DescribeWorkspaces",
                "dynamodb:BatchGetItem",
                "iam:GetRole",
                "sqs:GetQueueUrl",
                "fsx:ListTagsForResource",
                "sns:ListSubscriptionsByTopic",
                "datapipeline:ListPipelines",
                "lambda:ListFunctions",
                "dax:BatchGetItem",
                "glacier:ListTagsForVault",
                "dynamodb:Scan",
                "dynamodb:List*",
                "sqs:GetQueueAttributes",
                "cloudwatch:GetMetricStatistics",
                "dax:Describe*",
                "s3-object-lambda:Get*",
                "glacier:ListMultipartUploads",
                "application-autoscaling:DescribeScalingPolicies",
                "lambda:ListEventSourceMappings",
                "cloudwatch:DescribeAlarms",
                "eks:DescribeCluster",
                "resource-groups:ListGroupResources",
                "eks:ListClusters",
                "ec2:DescribeSubnets",
                "glacier:GetVaultLock",
                "glacier:ListVaults",
                "resource-groups:GetGroupQuery",
                "autoscaling:Describe*",
                "s3-object-lambda:List*",
                "tag:GetResources",
                "cloudwatch:GetMetricData",
                "glacier:DescribeVault",
                "sns:ListTopics",
                "ecr:ListImages",
                "datapipeline:DescribePipelines",
                "dax:Scan",
                "ec2:DescribeVpcAttribute",
                "cloudwatch:ListMetrics",
                "glacier:GetVaultNotifications",
                "glacier:DescribeJob",
                "glacier:GetDataRetrievalPolicy",
                "glacier:ListJobs",
                "cloudwatch:DescribeAlarmHistory",
                "dynamodb:PartiQLSelect",
                "ec2:DescribeAvailabilityZones",
                "application-autoscaling:DescribeScalingActivities",
                "kms:DescribeKey",
                "ecr:DescribeRepositories",
                "ecs:ListClusters",
                "dax:List*",
                "sqs:ListQueues",
                "sns:GetTopicAttributes",
                "application-autoscaling:DescribeScalableTargets",
                "datapipeline:QueryObjects",
                "iam:ListRoles",
                "datapipeline:DescribeObjects",
                "dynamodb:Query",
                "kinesis:DescribeStream",
                "resource-groups:ListGroups",
                "glacier:GetJobOutput",
                "datapipeline:GetPipelineDefinition",
                "elasticache:Describe*",
                "ec2:DescribeSecurityGroups",
                "sns:List*",
                "dax:GetItem",
                "ec2:Describe*",
                "sqs:ListDeadLetterSourceQueues",
                "rds:ListTagsForResource",
                "ec2:DescribeVpcs",
                "kms:ListAliases"
            ],
            "Resource": "*",
            "Effect": "Allow",
            "Sid": "VisualEditor1"
        }
    ]
}`,
    copied: false,
  },
  {
    name: 'Cost Explorer Policy',
    suggestedName: 'cfx-cost-explorer-policy',
    description:
      'This policy allows access to AWS Cost Explorer for cost analysis and reporting.',
    json: `{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Action": [
                "ce:GetCostAndUsage",
                "ce:GetCostForecast",
                "ce:GetReservationUtilization",
                "ce:GetRightsizingRecommendation",
                "ce:GetSavingsPlansCoverage",
                "ce:GetSavingsPlansPurchaseRecommendation",
                "ce:GetSavingsPlansUtilization",
                "ce:GetDimensionValues",
                "ce:GetTags"
            ],
            "Resource": "*",
            "Effect": "Allow"
        }
    ]
}`,
    copied: false,
  },
  {
    name: 'Resource Tagging Policy',
    suggestedName: 'cfx-resource-tagging-policy',
    description:
      'This policy allows managing resource tags across AWS services.',
    json: `{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Action": [
                "lambda:TagResource",
                "eks:ListTagsForResource",
                "sqs:UntagQueue",
                "elasticbeanstalk:AddTags",
                "resource-groups:Untag",
                "globalaccelerator:TagResource",
                "s3:DeleteJobTagging",
                "s3:PutObjectTagging",
                "resource-groups:Tag",
                "s3:DeleteObjectTagging",
                "elasticache:RemoveTagsFromResource",
                "ec2:CreateTags",
                "s3:DeleteStorageLensConfigurationTagging",
                "elasticbeanstalk:RemoveTags",
                "fsx:UntagResource",
                "elasticache:AddTagsToResource",
                "globalaccelerator:UntagResource",
                "s3:DeleteObjectVersionTagging",
                "workspaces:CreateTags",
                "elasticloadbalancing:AddTags",
                "rds:RemoveTagsFromResource",
                "sns:TagResource",
                "elasticfilesystem:UntagResource",
                "ec2:DeleteTags",
                "elasticfilesystem:ListTagsForResource",
                "s3:ReplicateTags",
                "sns:UntagResource",
                "lambda:UntagResource",
                "elasticfilesystem:DeleteTags",
                "elasticloadbalancing:RemoveTags",
                "dynamodb:TagResource",
                "lambda:ListTags",
                "s3:PutBucketTagging",
                "fsx:TagResource",
                "rds:AddTagsToResource",
                "elasticbeanstalk:UpdateTagsForResource",
                "dynamodb:UntagResource",
                "eks:UntagResource",
                "s3:PutStorageLensConfigurationTagging",
                "s3:PutObjectVersionTagging",
                "glacier:AddTagsToVault",
                "s3:PutJobTagging",
                "elasticfilesystem:TagResource",
                "elasticfilesystem:CreateTags",
                "sqs:TagQueue",
                "workspaces:DeleteTags",
                "glacier:RemoveTagsFromVault",
                "eks:TagResource",
                "elasticmapreduce:RemoveTags",
                "elasticmapreduce:AddTags"
            ],
            "Resource": "*",
            "Effect": "Allow",
            "Sid": "VisualEditor0"
        }
    ]
}`,
    copied: false,
  },
];

export const ROLE_TRUST_POLICY: PolicyData[] = [
  {
    name: 'CloudForestX Trust Policy',
    suggestedName: 'cfx-trust-relationship',
    description:
      'This trust policy allows CloudForestX to assume this role and manage resources in your account.',
    json: `{
    "Version": "2012-10-17",
    "Statement": [
        {
            "Effect": "Allow",
            "Principal": {
                "AWS": "arn:aws:iam::425964170654:root"
            },
            "Action": "sts:AssumeRole"
        }
    ]
}`,
    copied: false,
  },
];

// export const COST_REPORTS_STEPS = (s3BucketName: string): PolicyData[] => [
//   {
//     name: 'Navigate to Cost and Usage Reports',
//     suggestedName: '',
//     description:
//       'Open the AWS Billing and Cost Management console. In the navigation pane, choose "Cost & Usage Reports" and click the "Create report" button.',
//     copied: false,
//   },
//   {
//     name: 'Specify Report Details',
//     suggestedName: '',
//     description:
//       'Enter the report name. Keep all default content options checked (Account identifiers, Usage amount and unit, etc.). These options enable CloudForestX to properly analyze your cost data.',
//     copied: false,
//   },
//   {
//     name: 'Configure S3 Bucket',
//     suggestedName: '',
//     description: (
//       <>
//         Use the S3 bucket you created in the previous step:{' '}
//         <strong>{s3BucketName || '[your S3 bucket name]'}</strong>.
//         <br />
//         <br />
//         For the S3 path prefix, use <strong>"cost-reports"</strong> exactly as
//         shown.
//         <br />
//         <br />
//         Make sure to tick the checkbox labeled "The following default policy
//         will be applied to your bucket" to allow AWS to deliver reports to your
//         bucket.
//       </>
//     ),

//     copied: false,
//   },
//   {
//     name: 'Configure Report Settings',
//     suggestedName: '',
//     description:
//       'Select "Hourly" for time granularity, "Create new report version" for versioning, and "GZIP" for compression. These settings optimize the reports for CloudForestX analysis.',
//     copied: false,
//   },
//   {
//     name: 'Review and Create',
//     suggestedName: '',
//     description:
//       'Review your report configuration and click "Create report" to finish. Wait for the report to be set up (this may take up to 24 hours for the first report to appear).',
//     copied: false,
//   },
// ];

// export const S3_CREATION_STEPS = (bucketName: string): PolicyData[] => [
//   {
//     name: 'Navigate to S3 Console',
//     description:
//       'Open the AWS S3 Console and click "Create bucket" button in the top right corner.',
//     copied: false,
//   },
//   {
//     name: 'General Configuration',
//     suggestedName: bucketName,
//     description:
//       'Enter the exact bucket name shown below. This naming pattern is required for CloudForestX to access your cost and usage data.',
//     copied: false,
//   },
//   {
//     name: 'Block Public Access Settings',
//     description:
//       'Keep "Block all public access" checked. This ensures your cost data remains private.',
//     copied: false,
//   },
//   {
//     name: 'Bucket Versioning',
//     description:
//       'Enable bucket versioning to protect against accidental deletion of cost reports.',
//     copied: false,
//   },
//   {
//     name: 'Default Encryption',
//     description:
//       'Enable server-side encryption with Amazon S3 managed keys (SSE-S3).',
//     copied: false,
//   },
//   {
//     name: 'Advanced Settings',
//     description:
//       'Keep all other settings as default and click "Create bucket".',
//     copied: false,
//   },
// ];

export const COST_REPORTS_STEPS = (s3BucketName: string): PolicyData[] => [
  {
    name: 'Navigate to Cost and Usage Reports',
    suggestedName: '',
    description: (
      <>
        Open the AWS Billing and Cost Management console. In the navigation
        pane, choose <strong>Cost & Usage Reports</strong> and click the{' '}
        <strong>Create report</strong> button.
      </>
    ),
    copied: false,
  },
  {
    name: 'Specify Report Details',
    suggestedName: '',
    description: (
      <>
        Enter the report name. Keep all default content options checked (
        <strong>Account identifiers</strong>,{' '}
        <strong>Usage amount and unit</strong>, etc.). These options enable
        CloudForestX to properly analyze your cost data.
      </>
    ),
    copied: false,
  },
  {
    name: 'Configure S3 Bucket',
    suggestedName: 'cost-reports',
    description: (
      <>
        Use the S3 bucket you created in the previous step:{' '}
        <strong>{s3BucketName || '[your S3 bucket name]'}</strong>.
        <br />
        <br />
        For the S3 path prefix, use <strong>"cost-reports"</strong> exactly as
        shown.
        <br />
        <br />
        <strong>Important:</strong> Make sure to tick the checkbox labeled "The
        following default policy will be applied to your bucket" to allow AWS to
        deliver reports to your bucket.
      </>
    ),
    copied: false,
  },
  {
    name: 'Configure Report Settings',
    suggestedName: '',
    description: (
      <>
        Select <strong>Hourly</strong> for time granularity,{' '}
        <strong>Create new report version</strong> for versioning, and{' '}
        <strong>GZIP</strong> for compression. These settings optimize the
        reports for CloudForestX analysis.
      </>
    ),
    copied: false,
  },
  {
    name: 'Review and Create',
    suggestedName: '',
    description: (
      <>
        Review your report configuration and click{' '}
        <strong>Create report</strong> to finish. Wait for the report to be set
        up (this may take up to 24 hours for the first report to appear).
      </>
    ),
    copied: false,
  },
];

export const S3_CREATION_STEPS = (bucketName: string): PolicyData[] => [
  {
    name: 'Navigate to S3 Console',
    description: (
      <>
        Open the AWS S3 Console and click <strong>Create bucket</strong> button
        in the top right corner.
      </>
    ),
    copied: false,
    suggestedName: '',
  },
  {
    name: 'General Configuration',
    suggestedName: bucketName,
    description: (
      <>
        Enter the exact bucket name shown below. This naming pattern is required
        for CloudForestX to access your cost and usage data.
      </>
    ),
    copied: false,
  },
  {
    name: 'Block Public Access Settings',
    description: (
      <>
        Keep <strong>Block all public access</strong> checked. This ensures your
        cost data remains private.
      </>
    ),
    copied: false,
    suggestedName: '',
  },
  {
    name: 'Bucket Versioning',
    description: (
      <>
        Enable <strong>bucket versioning</strong> to protect against accidental
        deletion of cost reports.
      </>
    ),
    copied: false,
    suggestedName: '',
  },
  {
    name: 'Default Encryption',
    description: (
      <>
        Enable server-side encryption with{' '}
        <strong>Amazon S3 managed keys (SSE-S3)</strong>.
      </>
    ),
    copied: false,
    suggestedName: '',
  },
  {
    name: 'Advanced Settings',
    description: (
      <>
        Keep all other settings as default and click{' '}
        <strong>Create bucket</strong>.
      </>
    ),
    copied: false,
    suggestedName: '',
  },
];
